import { CommunicationResponse } from 'common/src/types';
import { useContext, useRef } from 'react';
import { PlaidHandler } from '../../../types/plaid';
import Modal from '../../modal';
import UI from '../../ui';
import Utils from '../../utils';
import { FundingRestrictedModal } from '../FundingRestrictedModal.component';

const { ModalContext } = Modal;
const { Spinner } = UI;
const { Communication } = Utils;

enum SessionStatus {
	CREATED = 'Created',
	ACTIVE = 'Active',
	SUCCESS = 'Success',
	PENDING_REVIEW = 'PendingReview',
	FAILED = 'Failed',
	EXPIRED = 'Expired',
	CANCELED = 'Canceled',
}

export type StartIdvSessionOptions = {
	gaveConsent: boolean;
	requestReverification?: boolean;
};

/** Best-effort snapshot of the IDV session at the moment a flow ends — null
 * fields mean no status was ever fetched for this attempt (e.g. cancelled
 * before Plaid returned any result). */
type VerificationResult = {
	status: SessionStatus | null;
	trustLevel: string | null;
};

type PollVerificationStatusOptions = {
	onSuccess?: (result: VerificationResult) => void;
	onFailure?: (result: VerificationResult) => void;
	maxAttempts?: number;
	pollIntervalMs?: number;
};

type IdvSessionResponse = {
	plaidLinkToken: string;
	status: SessionStatus;
};

type IdvStatusResponse = {
	status: SessionStatus;
	trustLevel: string | null;
};

type VerificationCallbacks = {
	onSuccess?: (result: VerificationResult) => void;
	onFailure?: (result: VerificationResult) => void;
	onCancel?: (result: VerificationResult) => void;
	onError?: (error: Error, result: VerificationResult) => void;
};

export type IdentityVerificationHook = {
	startVerification: (callbacks?: VerificationCallbacks) => Promise<void>;
};

const PLAID_SCRIPT_URL =
	'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

export const useIdentityVerification = (): IdentityVerificationHook => {
	const { showError, showModal, closeModal } = useContext(ModalContext);

	const isVerifying = useRef(false);
	const spinnerModalId = useRef<number | null>(null);
	const plaidHandler = useRef<PlaidHandler | null>(null);

	const loadPlaidScript = (): Promise<void> => {
		return new Promise((resolve, reject) => {
			if (window.Plaid) return resolve();

			const plaidScript = document.querySelector(
				`script[src="${PLAID_SCRIPT_URL}"]`
			);

			if (plaidScript) {
				const onLoad = () => {
					plaidScript.removeEventListener('load', onLoad);
					plaidScript.removeEventListener('error', onError);

					resolve();
				};

				const onError = () => {
					plaidScript.removeEventListener('load', onLoad);
					plaidScript.removeEventListener('error', onError);

					reject(new Error('Failed to load Plaid script'));
				};

				plaidScript.addEventListener('load', onLoad);
				plaidScript.addEventListener('error', onError);

				return;
			}

			const script = document.createElement('script');

			script.src = PLAID_SCRIPT_URL;
			script.async = true;
			script.onload = () => resolve();
			script.onerror = () => reject(new Error('Failed to load Plaid script'));

			document.body.appendChild(script);
		});
	};

	const showSpinner = () => {
		spinnerModalId.current = showModal({
			component: () => Spinner({ msg: 'Starting verification session...' }),
		});
	};

	const hideSpinner = () => {
		if (spinnerModalId.current !== null) {
			closeModal(spinnerModalId.current);
			spinnerModalId.current = null;
		}
	};

	const showFundingRestrictedModal = () => {
		showModal({
			component: FundingRestrictedModal,
		});
	};

	const pollVerificationStatus = async ({
		onSuccess,
		onFailure,
		maxAttempts = 3,
		pollIntervalMs = 1500,
	}: PollVerificationStatusOptions = {}): Promise<SessionStatus | null> => {
		for (let attempt = 0; attempt < maxAttempts; attempt++) {
			await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

			try {
				const response: CommunicationResponse<IdvStatusResponse> =
					await Communication.getIdvStatus();

				if (Communication.isSuccessful(response)) {
					const { status, trustLevel } = response.data;

					if (status === SessionStatus.ACTIVE) continue;

					if (status === SessionStatus.FAILED) {
						showFundingRestrictedModal();
						onFailure?.({ status, trustLevel });

						return status;
					}

					if (status === SessionStatus.SUCCESS) {
						onSuccess?.({ status, trustLevel });
					}

					return status;
				}
			} catch (error) {
				console.error('Error polling verification status:', error);
			}
		}

		showError({
			data: {
				title: 'Verification Error',
				description:
					'An error occurred during verification. Please try again or contact support.',
				primaryButtonText: 'Close',
			},
		});

		return null;
	};

	const startVerification = async (callbacks?: VerificationCallbacks) => {
		if (isVerifying.current) return;

		isVerifying.current = true;

		try {
			showSpinner();

			const sessionResponse: CommunicationResponse<IdvSessionResponse> =
				await Communication.startIdvSession({
					gaveConsent: true,
				});

			if (!Communication.isSuccessful(sessionResponse)) {
				throw new Error('Failed to start verification session');
			}

			const { plaidLinkToken, status } = sessionResponse.data;

			if (!plaidLinkToken) {
				throw new Error('No Plaid link token received');
			}

			if (status === SessionStatus.FAILED) {
				hideSpinner();
				showFundingRestrictedModal();

				return callbacks?.onError?.(
					new Error('Verification session already failed'),
					{ status, trustLevel: null }
				);
			}

			await loadPlaidScript();

			hideSpinner();

			if (!window.Plaid) {
				throw new Error('Plaid SDK not loaded');
			}

			if (plaidHandler.current) {
				plaidHandler.current.destroy();
				plaidHandler.current = null;
			}

			plaidHandler.current = window.Plaid.create({
				token: plaidLinkToken,
				onSuccess: async () => {
					await pollVerificationStatus({
						onSuccess: callbacks?.onSuccess,
						onFailure: callbacks?.onFailure,
					});
				},
				onExit: error => {
					if (error) {
						showError({
							data: {
								title: 'Verification Error',
								description:
									'An error occurred during verification. Please try again or contact support.',
								primaryButtonText: 'Close',
							},
						});

						return callbacks?.onError?.(
							new Error(
								'An error occurred during verification. Please try again or contact support.'
							),
							{ status: null, trustLevel: null }
						);
					}

					callbacks?.onCancel?.({ status: null, trustLevel: null });
				},
			});

			plaidHandler.current.open();
		} catch (error) {
			hideSpinner();

			const errorMessage =
				error instanceof Error ? error.message : 'An unexpected error occurred';

			showError({
				data: {
					title: 'Verification Error',
					description: `Failed to start verification: ${errorMessage}`,
					primaryButtonText: 'Close',
				},
			});

			callbacks?.onError?.(
				error instanceof Error ? error : new Error(errorMessage),
				{ status: null, trustLevel: null }
			);
		} finally {
			isVerifying.current = false;
		}
	};

	return {
		startVerification,
	};
};
